import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, checkUserAdminStatus } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  adminRole: string | null;
  hasMFA: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkForceLogoutManually: () => Promise<void>;
  checkAdminStatusManually: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const [hasMFA, setHasMFA] = useState(false);
  const lastForceLogoutCheck = useRef<number>(0);
  const forceLogoutCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const adminStatusCheckInterval = useRef<NodeJS.Timeout | null>(null);

  // Function to check for force logout signal
  const checkForceLogout = async (currentUser: User) => {
    try {
      // Get the latest user data to check metadata
      const { data: { user: freshUser }, error } = await supabase.auth.getUser();
      if (error || !freshUser) return;

      const forceLogoutTimestamp = freshUser.user_metadata?.force_logout;
      if (forceLogoutTimestamp && typeof forceLogoutTimestamp === 'number') {
        // Check if this is a new force logout signal
        if (forceLogoutTimestamp > lastForceLogoutCheck.current) {
          lastForceLogoutCheck.current = forceLogoutTimestamp;
          
          // Check if this is a maintenance mode force logout
          const isMaintenanceLogout = freshUser.user_metadata?.maintenance_force_logout;
          const initiatedBy = freshUser.user_metadata?.force_logout_initiated_by;
          
          let message = 'You have been logged out by an administrator.';
          if (isMaintenanceLogout && initiatedBy === 'maintenance_mode') {
            message = 'The application is currently undergoing maintenance. You have been logged out for your safety. Please try again later.';
          }
          
          // Show notification to user
          alert(message);
          
          // Sign out the user
          await supabase.auth.signOut();
          return;
        }
      }
    } catch (error) {
      // Error checking force logout
    }
  };

  // Function to start periodic force logout checks
  const startForceLogoutMonitoring = (currentUser: User) => {
    // Clear any existing interval
    if (forceLogoutCheckInterval.current) {
      clearInterval(forceLogoutCheckInterval.current);
    }

    // Store initial force logout timestamp if it exists
    if (currentUser.user_metadata?.force_logout) {
      lastForceLogoutCheck.current = currentUser.user_metadata.force_logout;
    }

    // Check every 20 seconds for force logout signals
    forceLogoutCheckInterval.current = setInterval(async () => {
      if (currentUser) {
        await checkForceLogout(currentUser);
      }
    }, 20000); // 20 seconds

    // Also check immediately
    checkForceLogout(currentUser);
  };

  // Function to start periodic admin status verification
  const startAdminStatusMonitoring = (currentUser: User) => {
    // Clear any existing interval
    if (adminStatusCheckInterval.current) {
      clearInterval(adminStatusCheckInterval.current);
    }

    // Check admin status every 5 minutes
    adminStatusCheckInterval.current = setInterval(async () => {
      if (currentUser && isAdmin) {
        try {
          const { isAdmin: currentAdminStatus } = await checkUserAdminStatus(currentUser.id);
          if (!currentAdminStatus) {
            await supabase.auth.signOut();
          }
        } catch (error) {
          // On error, sign out for security
          await supabase.auth.signOut();
        }
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Function to stop force logout monitoring
  const stopForceLogoutMonitoring = () => {
    if (forceLogoutCheckInterval.current) {
      clearInterval(forceLogoutCheckInterval.current);
      forceLogoutCheckInterval.current = null;
    }
  };

  // Function to stop admin status monitoring
  const stopAdminStatusMonitoring = () => {
    if (adminStatusCheckInterval.current) {
      clearInterval(adminStatusCheckInterval.current);
      adminStatusCheckInterval.current = null;
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Skip admin status check on email confirmation page to avoid 403 errors
        const isOnConfirmEmailPage = window.location.pathname === '/confirm-email';
        if (!isOnConfirmEmailPage) {
          checkAdminStatus(session.user.id);
          startAdminStatusMonitoring(session.user);
        }
        startForceLogoutMonitoring(session.user);
      }
      
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        // Skip admin status check on email confirmation page to avoid 403 errors
        const isOnConfirmEmailPage = window.location.pathname === '/confirm-email';
        if (!isOnConfirmEmailPage) {
          // Check admin status when user signs in (but not on email confirmation page)
          await checkAdminStatus(session.user.id);
          startAdminStatusMonitoring(session.user);
        }
        startForceLogoutMonitoring(session.user);
      } else if (event === 'SIGNED_OUT') {
        setIsAdmin(false);
        setAdminRole(null);
        setHasMFA(false);
        stopForceLogoutMonitoring();
        stopAdminStatusMonitoring();
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Check for force logout when token is refreshed (user might be active)
        await checkForceLogout(session.user);
      }
      
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      stopForceLogoutMonitoring();
      stopAdminStatusMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      // Skip admin status check on email confirmation page to avoid 403 errors
      const isOnConfirmEmailPage = window.location.pathname === '/confirm-email';
      if (isOnConfirmEmailPage) {
        return;
      }

      // Add rate limiting to prevent abuse
      const now = Date.now();
      const lastCheck = sessionStorage.getItem('lastAdminCheck');
      if (lastCheck && (now - parseInt(lastCheck)) < 5000) { // 5 second cooldown
        return;
      }
      sessionStorage.setItem('lastAdminCheck', now.toString());

      const { isAdmin, adminRole, hasMFA: mfaStatus } = await checkUserAdminStatus(userId);
      
      // Validate the response
      if (typeof isAdmin !== 'boolean' || (adminRole && typeof adminRole !== 'string')) {
        setIsAdmin(false);
        setAdminRole(null);
        setHasMFA(false);
        await supabase.auth.signOut();
        return;
      }

      setIsAdmin(isAdmin);
      setAdminRole(adminRole);
      setHasMFA(mfaStatus || false);
      
      // If user is not admin, sign them out immediately
      // BUT NOT if they're on the reset-password page, confirm-email page, or mobile app instructions page
      if (!isAdmin) {
        // Check if user is on a page where they should stay logged in
        const isOnResetPasswordPage = window.location.pathname === '/reset-password';
        const isOnConfirmEmailPage = window.location.pathname === '/confirm-email';
        const isOnMobileAppInstructionsPage = window.location.pathname === '/mobile-app-instructions';
        
        if (!isOnResetPasswordPage && !isOnConfirmEmailPage && !isOnMobileAppInstructionsPage) {
          await supabase.auth.signOut();
          
          // Clear any cached admin data
          sessionStorage.removeItem('lastAdminCheck');
          localStorage.removeItem('adminStatus');
        }
      } else {
        // Cache admin status for a short period (5 minutes)
        localStorage.setItem('adminStatus', JSON.stringify({
          isAdmin: true,
          adminRole,
          hasMFA: mfaStatus || false,
          timestamp: now,
          expiresAt: now + (5 * 60 * 1000)
        }));
      }
    } catch (error) {
      setIsAdmin(false);
      setAdminRole(null);
      setHasMFA(false);
      
      // On error, sign out for security
      await supabase.auth.signOut();
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      // Admin status check will be handled by the auth state change listener
    } catch (error) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      // Error signing out
    }
  };

  // Manual force logout check - can be called from components if needed
  const checkForceLogoutManually = async () => {
    if (user) {
      await checkForceLogout(user);
    }
  };

  // Manual admin status check - can be called from components if needed
  const checkAdminStatusManually = async () => {
    if (user) {
      await checkAdminStatus(user.id);
    }
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    adminRole,
    hasMFA,
    signIn,
    signOut,
    checkForceLogoutManually,
    checkAdminStatusManually,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
